import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, Link } from 'react-router-dom';
import { FaChevronDown, FaChevronUp, FaSearch, FaTimes } from 'react-icons/fa';
import axios from '../api/axios';
import { CUTOFF_START_YEAR, CUTOFF_END_YEAR } from '../constants/years';
import { SITE_CONFIG } from '../config/siteConfig';

const COMMUNITIES = ['OC', 'BC', 'BCM', 'MBC', 'SC', 'SCA', 'ST'];

const NAAC_COLOR: Record<string, string> = {
  'A++': 'bg-green-100 text-green-800',
  'A+':  'bg-blue-100 text-blue-800',
  'A':   'bg-yellow-100 text-yellow-800',
  'B++': 'bg-orange-100 text-orange-800',
  'B+':  'bg-orange-50 text-orange-700',
  'B':   'bg-gray-100 text-gray-700',
};

// Strip address but preserve Anna University campus identifier (CEG/MIT/ACT)
const fullName = (name: string) => {
  const campusMatch = name.match(/^(.*?)\s*[-–]\s*([A-Z]{2,5}\s*Campus)/i);
  if (campusMatch) {
    const base = campusMatch[1].split(',')[0].trim();
    return `${base} — ${campusMatch[2]}`;
  }
  return name.split(',')[0];
};

// Compact version used in the comparison table header
const shortName = (name: string) => {
  const full = fullName(name);
  return full.length > 35 ? full.substring(0, 35) + '…' : full;
};

const CutoffComparison = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [comparisonData, setComparisonData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCommunity, setSelectedCommunity] = useState('OC');
  const [userCutoff, setUserCutoff] = useState<number | null>(null);
  const [userRank, setUserRank] = useState<number | null>(null);
  const [branchPreference, setBranchPreference] = useState<string | null>(null);
  const [showCourses, setShowCourses] = useState(false);
  const [courseViewMode, setCourseViewMode] = useState<'single' | 'multi'>('single');

  // Manual search (when no URL params)
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedColleges, setSelectedColleges] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [directInputType, setDirectInputType] = useState<'cutoff' | 'rank'>('cutoff');
  const [directCutoff, setDirectCutoff] = useState('');
  const [directRank, setDirectRank] = useState('');

  const fromPredictor = !!searchParams.get('colleges');

  const getCollegeIds = () =>
    selectedColleges.length > 0
      ? selectedColleges.map(c => c.id).join(',')
      : searchParams.get('colleges') || '';

  useEffect(() => {
    const collegesParam = searchParams.get('colleges');
    const communityParam = searchParams.get('community');
    const cutoffParam = searchParams.get('cutoff');
    const rankParam = searchParams.get('rank');

    if (collegesParam) {
      if (communityParam) setSelectedCommunity(communityParam);
      if (cutoffParam) setUserCutoff(parseFloat(cutoffParam));
      if (rankParam) setUserRank(parseInt(rankParam));
      const branchParam = searchParams.get('branch');
      if (branchParam) setBranchPreference(branchParam);
      fetchComparison(collegesParam, communityParam || 'OC');
    } else {
      setLoading(false);
    }
  }, []);

  const fetchComparison = async (collegeIds: string, community: string) => {
    if (!collegeIds) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/cutoffs/compare-colleges', {
        params: { colleges: collegeIds, community, start_year: CUTOFF_START_YEAR, end_year: CUTOFF_END_YEAR },
      });
      if (response?.data) setComparisonData(response.data);
      else setComparisonData(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch comparison data');
    } finally {
      setLoading(false);
    }
  };

  const handleCommunitySwitch = (comm: string) => {
    setSelectedCommunity(comm);
    fetchComparison(getCollegeIds(), comm);
  };

  const searchColleges = async (term: string) => {
    if (term.length < 2) { setSearchResults([]); return; }
    try {
      const res = await axios.get('/colleges', { params: { search: term, limit: 10 } });
      setSearchResults(res.data.colleges || []);
    } catch {}
  };

  const handleCompare = () => {
    if (selectedColleges.length < 2) return;
    const ids = selectedColleges.map(c => c.id).join(',');
    if (directInputType === 'cutoff' && directCutoff) setUserCutoff(parseFloat(directCutoff));
    if (directInputType === 'rank' && directRank) setUserRank(parseInt(directRank));
    setSearchParams({ colleges: ids, community: selectedCommunity });
    fetchComparison(ids, selectedCommunity);
  };

  const colleges = comparisonData?.comparison || [];

  // Find the best course for a college given user's cutoff/rank and branch preference
  const getBestCourse = (item: any) => {
    const courses: any[] = item.courses || [];
    if (!courses.length) return null;

    // 1. If branch preference exists and college offers it, use that
    if (branchPreference) {
      const preferred = courses.find((c: any) => c.branch_code === branchPreference);
      if (preferred) return preferred;
    }

    // 2. Find best qualifying course (highest cutoff student can get)
    const qualifying = courses.filter((c: any) => {
      const cutoff = c.latest_year?.cutoff_mark;
      const rank = c.latest_year?.closing_rank;
      if (userCutoff && cutoff) return userCutoff >= cutoff;
      if (userRank && rank) return userRank <= rank;
      return true;
    });

    // Among qualifying, pick highest cutoff (most competitive = best branch)
    if (qualifying.length) {
      return qualifying.sort((a: any, b: any) =>
        (b.latest_year?.cutoff_mark ?? 0) - (a.latest_year?.cutoff_mark ?? 0)
      )[0];
    }

    // 3. Fallback: course with highest cutoff regardless
    return courses.sort((a: any, b: any) =>
      (b.latest_year?.cutoff_mark ?? 0) - (a.latest_year?.cutoff_mark ?? 0)
    )[0];
  };

  // ── Summary card helpers ─────────────────────────────────────────────────
  const bestPlacement = colleges.length
    ? colleges.filter((i: any) => i.college.placement_info && !isNaN(parseFloat(i.college.placement_info)))
        .sort((a: any, b: any) => parseFloat(b.college.placement_info) - parseFloat(a.college.placement_info))[0]
    : null;

  const mostCompetitive = colleges.length
    ? colleges.filter((i: any) => i.courses?.[0]?.latest_year?.closing_rank)
        .sort((a: any, b: any) => a.courses[0].latest_year.closing_rank - b.courses[0].latest_year.closing_rank)[0]
    : null;

  const naacOrder: Record<string, number> = { 'A++': 1, 'A+': 2, 'A': 3, 'B++': 4, 'B+': 5, 'B': 6 };
  const bestNaac = colleges.length
    ? colleges.filter((i: any) => i.college.naac_grade)
        .sort((a: any, b: any) => (naacOrder[a.college.naac_grade] ?? 99) - (naacOrder[b.college.naac_grade] ?? 99))[0]
    : null;

  return (
    <>
      <Helmet>
        <title>{`TNEA ${SITE_CONFIG.year} Cutoff Comparison — Side-by-Side College Comparison Tool | ChooseMyCollege`}</title>
        <meta name="description" content={`Compare Tamil Nadu engineering colleges side by side for TNEA ${SITE_CONFIG.year}. View ${CUTOFF_END_YEAR} cutoff marks, closing ranks, annual fees, hostel charges, transport and NAAC grades — all in one place. Up to 5 colleges.`} />
        <meta name="keywords" content={`compare engineering colleges Tamil Nadu, TNEA ${SITE_CONFIG.year} college comparison, TNEA cutoff comparison, college fees comparison Tamil Nadu, TNEA rank vs cutoff ${CUTOFF_END_YEAR}`} />
        <meta property="og:title" content={`TNEA ${SITE_CONFIG.year} Cutoff Comparison — Side-by-Side Tool | ChooseMyCollege`} />
        <meta property="og:description" content={`Compare up to 5 Tamil Nadu engineering colleges side by side — ${CUTOFF_END_YEAR} closing ranks, fees, hostel and NAAC grades.`} />
        <meta property="og:url" content="https://choosemycollege.in/cutoffs" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://choosemycollege.in/cutoffs" />
      </Helmet>

      <div className="max-w-7xl mx-auto">


        {/* Breadcrumb */}
        <nav className="text-xs text-gray-500 mb-4">
          <Link to="/predictor" className="hover:text-blue-600">College Predictor</Link>
          <span className="mx-1.5">›</span>
          <span className="text-gray-800 font-medium">Compare Colleges</span>
        </nav>

        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">Compare Colleges</h1>

        {/* ── Manual search (only shown when NOT coming from predictor) ── */}
        {!fromPredictor && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-6">
            <h2 className="text-base font-semibold text-gray-700 mb-3">Select colleges to compare</h2>

            {/* Search input */}
            <div className="relative mb-3">
              <FaSearch className="absolute left-3 top-3 text-gray-400 text-xs" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setShowDropdown(true); searchColleges(e.target.value); }}
                placeholder="Search by college name or code…"
                disabled={selectedColleges.length >= 5}
                className="w-full pl-8 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
              />
              {showDropdown && searchResults.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                  {searchResults.map((c: any) => (
                    <button
                      key={c.id}
                      onClick={() => {
                        if (!selectedColleges.find(s => s.id === c.id)) setSelectedColleges(p => [...p, c]);
                        setSearchTerm(''); setSearchResults([]); setShowDropdown(false);
                      }}
                      disabled={selectedColleges.some(s => s.id === c.id)}
                      className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-gray-50 last:border-0 text-sm disabled:opacity-40"
                    >
                      <div className="font-medium text-gray-800">{fullName(c.college_name)}</div>
                      <div className="text-xs text-gray-400">{c.college_code} · {c.district?.name}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected pills */}
            {selectedColleges.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedColleges.map((c: any) => (
                  <span key={c.id} className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1 rounded-full text-xs font-medium">
                    {c.college_code}
                    <button onClick={() => setSelectedColleges(p => p.filter(s => s.id !== c.id))} className="hover:text-red-500">
                      <FaTimes className="text-[10px]" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Cutoff / Rank input */}
            <div className="border border-gray-100 rounded-lg p-3 mb-3 bg-gray-50">
              <p className="text-xs text-gray-500 mb-2 font-medium">Enter your Mark / Rank to check if you qualify <span className="text-gray-400 font-normal">(optional)</span></p>
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => setDirectInputType('cutoff')}
                  className={`flex-1 py-1.5 text-xs rounded-md font-medium transition-colors ${directInputType === 'cutoff' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  Cutoff Mark
                </button>
                <button
                  onClick={() => setDirectInputType('rank')}
                  className={`flex-1 py-1.5 text-xs rounded-md font-medium transition-colors ${directInputType === 'rank' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  Rank
                </button>
              </div>
              {directInputType === 'cutoff'
                ? <input type="number" value={directCutoff} onChange={e => setDirectCutoff(e.target.value)} placeholder="e.g. 172.5" className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                : <input type="number" value={directRank} onChange={e => setDirectRank(e.target.value)} placeholder="e.g. 5200" className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
              }
            </div>

            <button
              onClick={handleCompare}
              disabled={selectedColleges.length < 2}
              className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {selectedColleges.length < 2 ? `Select ${2 - selectedColleges.length} more college${selectedColleges.length === 1 ? '' : 's'}` : `Compare ${selectedColleges.length} Colleges`}
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4" />
            <p className="text-sm text-gray-500">Loading comparison…</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">{error}</div>
        )}

        {!loading && !error && colleges.length > 0 && (
          <>
            {/* ── Community switcher ── */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-5 flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mr-1">Community</span>
              {COMMUNITIES.map(comm => (
                <button
                  key={comm}
                  onClick={() => handleCommunitySwitch(comm)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    selectedCommunity === comm ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {comm}
                </button>
              ))}
              {(userCutoff || userRank) && (
                <span className="ml-auto text-xs text-gray-500">
                  Your {userCutoff ? `cutoff: ${userCutoff}` : `rank: ${userRank?.toLocaleString()}`}
                </span>
              )}
            </div>

            {/* ── Summary cards ── */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
              {bestPlacement && (
                <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                  <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Best Placement</div>
                  <div className="text-2xl font-bold text-green-700">{bestPlacement.college.placement_info}</div>
                  <div className="text-xs text-gray-500 mt-1 truncate">{shortName(bestPlacement.college.college_name)}</div>
                </div>
              )}
              {mostCompetitive && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Most Competitive</div>
                  <div className="text-2xl font-bold text-blue-700">Rank {mostCompetitive.courses[0].latest_year.closing_rank.toLocaleString()}</div>
                  <div className="text-xs text-gray-500 mt-1 truncate">{shortName(mostCompetitive.college.college_name)}</div>
                </div>
              )}
              {bestNaac && (
                <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4">
                  <div className="text-xs font-semibold text-yellow-600 uppercase tracking-wide mb-1">Best Accreditation</div>
                  <div className="text-2xl font-bold text-yellow-700">{bestNaac.college.naac_grade}</div>
                  <div className="text-xs text-gray-500 mt-1 truncate">{shortName(bestNaac.college.college_name)}</div>
                </div>
              )}
            </div>

            {/* ── Main comparison table ── */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-5 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="sticky left-0 bg-gray-50 text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">
                        College
                      </th>
                      {colleges.map((item: any) => (
                        <th key={item.college.id} className="text-left py-3 px-4 min-w-[180px]">
                          <Link to={`/colleges/${item.college.college_code}`} className="font-semibold text-gray-800 text-sm hover:text-blue-700 hover:underline leading-snug block">
                            {shortName(item.college.college_name)}
                          </Link>
                          <div className="text-xs text-gray-400 font-normal mt-0.5">{item.college.district?.name} · {item.college.college_code}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">

                    {/* Type */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="sticky left-0 bg-white py-3 px-4 text-xs font-medium text-gray-500">Type</td>
                      {colleges.map((item: any) => (
                        <td key={item.college.id} className="py-3 px-4 text-sm text-gray-700">
                          {item.college.college_type || '—'}
                          {item.college.autonomous_status && (
                            <span className="ml-1.5 px-1.5 py-0.5 bg-purple-50 text-purple-600 text-[10px] font-medium rounded">Autonomous</span>
                          )}
                        </td>
                      ))}
                    </tr>

                    {/* NAAC */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="sticky left-0 bg-white py-3 px-4 text-xs font-medium text-gray-500">NAAC</td>
                      {colleges.map((item: any) => (
                        <td key={item.college.id} className="py-3 px-4">
                          {item.college.naac_grade
                            ? <span className={`px-2 py-0.5 rounded text-xs font-semibold ${NAAC_COLOR[item.college.naac_grade] || 'bg-gray-100 text-gray-700'}`}>{item.college.naac_grade}</span>
                            : <span className="text-gray-300 text-xs">—</span>}
                        </td>
                      ))}
                    </tr>

                    {/* Best matching course name */}
                    <tr className="hover:bg-gray-50/50 bg-blue-50/30">
                      <td className="sticky left-0 bg-blue-50/30 py-3 px-4 text-xs font-medium text-gray-500">
                        {branchPreference ? 'Your Branch' : 'Best Match'}
                      </td>
                      {colleges.map((item: any) => {
                        const course = getBestCourse(item);
                        return (
                          <td key={item.college.id} className="py-3 px-4">
                            {course
                              ? <span className="inline-block px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded">
                                  {course.branch_name || course.course_name || course.branch_code}
                                </span>
                              : <span className="text-gray-300 text-xs">—</span>}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Cutoff needed */}
                    <tr className="hover:bg-gray-50/50 bg-blue-50/30">
                      <td className="sticky left-0 bg-blue-50/30 py-3 px-4 text-xs font-medium text-gray-500">Cutoff Needed</td>
                      {colleges.map((item: any) => {
                        const course = getBestCourse(item);
                        const cutoff = course?.latest_year?.cutoff_mark;
                        const qualifies = userCutoff && cutoff && userCutoff >= cutoff;
                        return (
                          <td key={item.college.id} className="py-3 px-4">
                            {cutoff
                              ? <span className={`text-sm font-bold ${qualifies ? 'text-green-600' : userCutoff ? 'text-red-500' : 'text-gray-800'}`}>{cutoff}</span>
                              : <span className="text-gray-300 text-xs">—</span>}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Closing rank */}
                    <tr className="hover:bg-gray-50/50 bg-blue-50/30">
                      <td className="sticky left-0 bg-blue-50/30 py-3 px-4 text-xs font-medium text-gray-500">Closing Rank</td>
                      {colleges.map((item: any) => {
                        const course = getBestCourse(item);
                        const rank = course?.latest_year?.closing_rank;
                        const qualifies = userRank && rank && userRank <= rank;
                        return (
                          <td key={item.college.id} className="py-3 px-4">
                            {rank
                              ? <span className={`text-sm font-bold ${qualifies ? 'text-green-600' : userRank ? 'text-red-500' : 'text-gray-800'}`}>{rank.toLocaleString()}</span>
                              : <span className="text-gray-300 text-xs">—</span>}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Placement */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="sticky left-0 bg-white py-3 px-4 text-xs font-medium text-gray-500">Placement</td>
                      {colleges.map((item: any) => (
                        <td key={item.college.id} className="py-3 px-4 text-sm font-semibold text-gray-700">
                          {item.college.placement_info || <span className="text-gray-300 text-xs">—</span>}
                        </td>
                      ))}
                    </tr>

                    {/* Annual Fee */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="sticky left-0 bg-white py-3 px-4 text-xs font-medium text-gray-500">Annual Fee</td>
                      {colleges.map((item: any) => {
                        const annual = parseFloat(item.college.annual_tuition_fee);
                        const dev = parseFloat(item.college.development_fee);
                        return (
                          <td key={item.college.id} className="py-3 px-4 text-sm text-gray-700">
                            {!isNaN(annual) ? (
                              <div className="space-y-0.5">
                                <div>₹{annual.toLocaleString('en-IN')}<span className="text-xs text-gray-400">/yr</span></div>
                                {!isNaN(dev) && <div className="text-xs text-gray-400">+₹{dev.toLocaleString('en-IN')} dev fee</div>}
                              </div>
                            ) : <span className="text-xs text-amber-600 font-medium">Contact college</span>}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Transport */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="sticky left-0 bg-white py-3 px-4 text-xs font-medium text-gray-500">Transport</td>
                      {colleges.map((item: any) => {
                        const available = item.college.transport_facilities;
                        const min = item.college.min_transport_charges;
                        const max = item.college.max_transport_charges;
                        return (
                          <td key={item.college.id} className="py-3 px-4 text-xs">
                            {available
                              ? <div>
                                  <span className="text-green-600 font-medium">✓ Available</span>
                                  {min && <div className="text-gray-500 mt-0.5">₹{Math.round(min).toLocaleString('en-IN')}{max ? `–${Math.round(max).toLocaleString('en-IN')}` : ''}/yr</div>}
                                </div>
                              : <span className="text-gray-300">—</span>}
                          </td>
                        );
                      })}
                    </tr>

                    {/* Hostel */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="sticky left-0 bg-white py-3 px-4 text-xs font-medium text-gray-500">Hostel</td>
                      {colleges.map((item: any) => {
                        const c = item.college;
                        const isPrivate = c.college_type === 'Private';
                        const toAnnual = (val: any, threshold: number) => { const n = parseFloat(val); return isNaN(n) ? null : (isPrivate && n <= threshold ? n * 12 : n); };
                        const boysCost = (() => { const r = toAnnual(c.boys_room_rent, 6000); const e = toAnnual(c.boys_electricity_charges, 2000); return (r != null || e != null) ? (r ?? 0) + (e ?? 0) : null; })();
                        const girlsCost = (() => { const r = toAnnual(c.girls_room_rent, 6000); const e = toAnnual(c.girls_electricity_charges, 2000); return (r != null || e != null) ? (r ?? 0) + (e ?? 0) : null; })();
                        const merged = c.boys_accommodation_available && c.girls_accommodation_available && boysCost === girlsCost;
                        return (
                          <td key={c.id} className="py-3 px-4">
                            {!c.boys_accommodation_available && !c.girls_accommodation_available
                              ? <span className="text-gray-300 text-xs">—</span>
                              : <div className="flex flex-col gap-1 text-xs">
                                  {merged ? (
                                    <div>
                                      <span className="text-green-600 font-medium">✓ Boys & Girls</span>
                                      {boysCost != null
                                        ? <div className="text-gray-500 mt-0.5">₹{boysCost.toLocaleString('en-IN')}/yr</div>
                                        : <div className="text-amber-600 text-xs mt-0.5">Contact college</div>}
                                    </div>
                                  ) : (
                                    <>
                                      {c.boys_accommodation_available && (
                                        <div>
                                          <span className="text-blue-600 font-medium">✓ Boys</span>
                                          {boysCost != null
                                            ? <div className="text-gray-500 mt-0.5">₹{boysCost.toLocaleString('en-IN')}/yr</div>
                                            : <div className="text-amber-600 text-xs mt-0.5">Contact college</div>}
                                        </div>
                                      )}
                                      {c.girls_accommodation_available && (
                                        <div>
                                          <span className="text-pink-600 font-medium">✓ Girls</span>
                                          {girlsCost != null
                                            ? <div className="text-gray-500 mt-0.5">₹{girlsCost.toLocaleString('en-IN')}/yr</div>
                                            : <div className="text-amber-600 text-xs mt-0.5">Contact college</div>}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                            }
                          </td>
                        );
                      })}
                    </tr>

                    {/* Mess */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="sticky left-0 bg-white py-3 px-4 text-xs font-medium text-gray-500">Mess</td>
                      {colleges.map((item: any) => {
                        const c = item.college;
                        const messLabel = (type: string) => {
                          if (type === 'Veg') return <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">🌱 Veg</span>;
                          if (type === 'Non-Veg') return <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">🍗 Non-Veg</span>;
                          return <span className="text-xs text-gray-500">{type}</span>;
                        };
                        const boys = c.boys_mess_type;
                        const girls = c.girls_mess_type;
                        const same = boys === girls;
                        return (
                          <td key={c.id} className="py-3 px-4 text-xs">
                            {!boys && !girls
                              ? <span className="text-gray-300">—</span>
                              : same && boys
                                ? messLabel(boys)
                                : <div className="flex flex-col gap-1">
                                    {boys && <div className="flex items-center gap-1"><span className="text-gray-400">👦</span>{messLabel(boys)}</div>}
                                    {girls && <div className="flex items-center gap-1"><span className="text-gray-400">👧</span>{messLabel(girls)}</div>}
                                  </div>
                            }
                          </td>
                        );
                      })}
                    </tr>

                    {/* Website */}
                    <tr className="hover:bg-gray-50/50">
                      <td className="sticky left-0 bg-white py-3 px-4 text-xs font-medium text-gray-500">Website</td>
                      {colleges.map((item: any) => {
                        const url = item.college.website;
                        const href = url ? (url.startsWith('http') ? url : `https://${url}`) : null;
                        return (
                          <td key={item.college.id} className="py-3 px-4 text-xs">
                            {href
                              ? <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block max-w-[150px]">
                                  {url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                                </a>
                              : <span className="text-gray-300">—</span>}
                          </td>
                        );
                      })}
                    </tr>

                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Courses section (collapsed by default) ── */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6">
              {/* Header row */}
              <div className="flex items-center justify-between px-5 py-4">
                <button
                  onClick={() => setShowCourses(!showCourses)}
                  className="flex items-center gap-2 text-left"
                >
                  <span className="font-semibold text-gray-800 text-sm">Courses Available</span>
                  {showCourses ? <FaChevronUp className="text-gray-400 text-xs" /> : <FaChevronDown className="text-gray-400 text-xs" />}
                </button>

                {/* Single / Multi toggle — only visible when open */}
                {showCourses && (
                  <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
                    <button
                      onClick={() => setCourseViewMode('single')}
                      className={`px-3 py-1.5 font-medium transition-colors ${courseViewMode === 'single' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                    >
                      Latest Year
                    </button>
                    <button
                      onClick={() => setCourseViewMode('multi')}
                      className={`px-3 py-1.5 font-medium transition-colors ${courseViewMode === 'multi' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                    >
                      5-Year Trend
                    </button>
                  </div>
                )}
              </div>

              {showCourses && (
                <div className="border-t border-gray-50 overflow-x-auto">
                  {(() => {
                    // Rank color helper
                    const rankColor = (rank: number) => {
                      if (rank < 1000) return 'text-green-700';
                      if (rank < 5000) return 'text-blue-700';
                      if (rank < 10000) return 'text-orange-600';
                      return 'text-red-600';
                    };

                    // Trend badge helper
                    const trendBadge = (trend: string) => {
                      if (trend === 'easier') return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-medium">↗ Easier</span>;
                      if (trend === 'harder') return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-medium">↘ Harder</span>;
                      return <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[10px] font-medium">→ Stable</span>;
                    };

                    // Collect all unique branch codes
                    const allBranches: Map<string, string> = new Map();
                    colleges.forEach((item: any) => {
                      item.courses?.forEach((c: any) => {
                        if (!allBranches.has(c.branch_code)) {
                          allBranches.set(c.branch_code, c.branch_name || c.course_name || c.branch_code);
                        }
                      });
                    });

                    return (
                      <table className="min-w-full">
                        <thead>
                          <tr className="bg-gray-50/50 border-b border-gray-100">
                            <th className="sticky left-0 bg-gray-50 text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide w-48">Department</th>
                            {colleges.map((item: any) => (
                              <th key={item.college.id} className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 min-w-[160px]">
                                {item.college.college_code}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {Array.from(allBranches.entries()).map(([code, name]) => (
                            <tr key={code} className="hover:bg-gray-50/30">
                              <td className="sticky left-0 bg-white py-3 px-4 text-xs font-medium text-gray-600 align-top">{name}</td>

                              {colleges.map((item: any) => {
                                const course = item.courses?.find((c: any) => c.branch_code === code);
                                if (!course) return <td key={item.college.id} className="py-3 px-4 text-gray-200 text-sm">—</td>;

                                const latest = course.latest_year;

                                /* ── Single year ── */
                                if (courseViewMode === 'single') {
                                  return (
                                    <td key={item.college.id} className="py-3 px-4 align-top">
                                      <div className="text-xs">
                                        <span className="font-bold text-gray-800">{latest?.cutoff_mark ?? '—'}</span>
                                        {latest?.closing_rank && (
                                          <span className={`ml-1.5 font-semibold ${rankColor(latest.closing_rank)}`}>
                                            Rank {latest.closing_rank.toLocaleString()}
                                          </span>
                                        )}
                                        {course.trend && <div className="mt-1">{trendBadge(course.trend)}</div>}
                                      </div>
                                    </td>
                                  );
                                }

                                /* ── Multi year ── */
                                const years = [...(course.years || [])]
                                  .filter((y: any) => y.cutoff_mark || y.closing_rank)
                                  .sort((a: any, b: any) => b.year - a.year);
                                return (
                                  <td key={item.college.id} className="py-3 px-4 align-top">
                                    {course.trend && <div className="mb-2">{trendBadge(course.trend)}</div>}
                                    <div className="space-y-0.5">
                                      {years.map((y: any, idx: number) => (
                                        <div key={y.year} className="flex items-baseline gap-1.5 text-xs">
                                          <span className={`w-9 flex-shrink-0 font-semibold ${idx === 0 ? 'text-blue-600' : 'text-gray-400'}`}>{y.year}</span>
                                          <span className="font-bold text-gray-800">{y.cutoff_mark ?? '—'}</span>
                                          {y.closing_rank && (
                                            <span className={`font-medium ${rankColor(y.closing_rank)}`}>
                                              · {y.closing_rank.toLocaleString()}
                                            </span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* ── Footer ── */}
            <div className="flex items-center justify-between pb-4">
              <Link to="/predictor" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                ← Back to Predictor
              </Link>
              <p className="text-xs text-gray-400">Cutoff data from {comparisonData?.year_range || `${CUTOFF_START_YEAR}–${CUTOFF_END_YEAR}`} · {selectedCommunity} community</p>
            </div>
          </>
        )}

        {/* No data state */}
        {!loading && !error && colleges.length === 0 && comparisonData && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm text-center py-16">
            <p className="text-gray-500 text-sm mb-4">No cutoff data found for <strong>{selectedCommunity}</strong> community.</p>
            <Link to="/predictor" className="text-sm text-blue-600 hover:underline">← Back to Predictor</Link>
          </div>
        )}
      </div>
    </>
  );
};

export default CutoffComparison;
